/**
 * Cognito's SRP-6a, client half — the arithmetic behind `USER_SRP_AUTH`.
 *
 * **Why SRP rather than a password grant** (D52 addendum, 2026-09-27). SRP proves the
 * person knows the password without the password leaving the device: Cognito receives a
 * public ephemeral and a signature, and nothing it logs, and nothing a TLS-terminating
 * proxy could read, can be replayed as the password. `USER_PASSWORD_AUTH` would send it
 * in the request body. The app client allows `userSrp` and nothing weaker.
 *
 * **Why written here rather than taken from `amazon-cognito-identity-js`.** That library
 * is in maintenance behind Amplify, pulls `buffer`, `js-cookie` and a fetch polyfill, and
 * needs `react-native-get-random-values` patched into the global before it will run on
 * Hermes — four dependencies, one of them a global mutation, for ~100 lines of arithmetic.
 * And the packages the app shares must stay dependency-free (`apps/mobile/metro.config.js`).
 * **This is not new cryptography**: SHA-256, randomness and the modular arithmetic are
 * the platform's (Web Crypto / `expo-crypto`, native `BigInt`); what lives here is only
 * Cognito's arrangement of them, and the only test that matters for an arrangement is
 * the real pool accepting it — `e2e/native-sign-in.e2e.ts`. The unit tests prove the
 * two halves of the exchange agree, which is what catches a transcription error.
 *
 * **The primitives are injected** because they differ by platform and must not be
 * reimplemented: `crypto.subtle` in Node and browsers, `expo-crypto` on the phone
 * (Hermes has no Web Crypto).
 */

/**
 * Bytes backed by an ordinary `ArrayBuffer` — what `expo-crypto`'s digest, like Web
 * Crypto's, accepts. A bare `Uint8Array` may be a view of a `SharedArrayBuffer`.
 */
export type Bytes = Uint8Array<ArrayBuffer>;

export interface SrpCrypto {
  sha256(data: Bytes): Promise<Uint8Array>;
  /** Cryptographically secure. The whole exchange is only as private as `a`. */
  randomBytes(length: number): Uint8Array;
}

/**
 * RFC 3526's 3072-bit MODP group — Cognito's `N`. A spec asserts it against Node's own
 * copy (`getDiffieHellman('modp15')`), so a transposed digit is a test failure rather
 * than a pool that rejects every sign-in with the same message a wrong password gets.
 */
export const N = BigInt(
  '0x' +
    'FFFFFFFFFFFFFFFFC90FDAA22168C234C4C6628B80DC1CD129024E088A67CC74' +
    '020BBEA63B139B22514A08798E3404DDEF9519B3CD3A431B302B0A6DF25F1437' +
    '4FE1356D6D51C245E485B576625E7EC6F44C42E9A637ED6B0BFF5CB6F406B7ED' +
    'EE386BFB5A899FA5AE9F24117C4B1FE649286651ECE45B3DC2007CB8A163BF05' +
    '98DA48361C55D39A69163FA8FD24CF5F83655D23DCA3AD961C62F356208552BB' +
    '9ED529077096966D670C354E4ABC9804F1746C08CA18217C32905E462E36CE3B' +
    'E39E772C180E86039B2783A2EC07A28FB5C55DF06F4C52C9DE2BCBF695581718' +
    '3995497CEA956AE515D2261898FA051015728E5A8AAAC42DAD33170D04507A33' +
    'A85521ABDF1CBA64ECFB850458DBEF0A8AEA71575D060C7DB3970F85A6E1E4C7' +
    'ABF5AE8CDB0933D71E8C94E04A25619DCEE3D2261AD2EE6BF12FFA06D98A0864' +
    'D87602733EC86A64521F2B18177B200CBBE117577A615D6C770988C0BAD946E2' +
    '08E24FA074E5AB3143DB5BFCE0FD108E4B82D120A93AD2CAFFFFFFFFFFFFFFFF',
);

export const g = 2n;

const utf8 = (value: string): Bytes => new TextEncoder().encode(value);

export function hexToBytes(hex: string): Bytes {
  const even = hex.length % 2 ? `0${hex}` : hex;
  const bytes = new Uint8Array(even.length / 2);
  for (let index = 0; index < bytes.length; index += 1) {
    bytes[index] = Number.parseInt(even.slice(index * 2, index * 2 + 2), 16);
  }
  return bytes;
}

export function bytesToHex(bytes: Uint8Array): string {
  let hex = '';
  for (const byte of bytes) hex += byte.toString(16).padStart(2, '0');
  return hex;
}

/**
 * A non-negative integer as the hex Cognito hashes: even length, and a leading `00` when
 * the top bit is set, so the bytes read as a positive two's-complement number.
 *
 * **This is where hand-written Cognito SRP usually breaks**, and it breaks one sign-in in
 * sixteen rather than all of them — only when a value happens to start with `8`–`f` —
 * which looks like a flaky password field rather than a bug.
 */
export function padHex(value: bigint): string {
  if (value < 0n) throw new Error('padHex takes a non-negative integer');
  const hex = value.toString(16);
  if (hex.length % 2) return `0${hex}`;
  return '89abcdef'.includes(hex[0] ?? '') ? `00${hex}` : hex;
}

export function modPow(base: bigint, exponent: bigint, modulus: bigint): bigint {
  let result = 1n;
  let square = ((base % modulus) + modulus) % modulus;
  let remaining = exponent;
  while (remaining > 0n) {
    if (remaining & 1n) result = (result * square) % modulus;
    square = (square * square) % modulus;
    remaining >>= 1n;
  }
  return result;
}

function concat(...parts: Uint8Array[]): Bytes {
  const out = new Uint8Array(parts.reduce((total, part) => total + part.length, 0));
  let offset = 0;
  for (const part of parts) {
    out.set(part, offset);
    offset += part.length;
  }
  return out;
}

/** RFC 2104 over the injected SHA-256 — `expo-crypto` offers a digest and no HMAC. */
export async function hmacSha256(crypto: SrpCrypto, key: Uint8Array, message: Uint8Array): Promise<Uint8Array> {
  const block = 64;
  const shortKey = key.length > block ? await crypto.sha256(Uint8Array.from(key)) : key;
  const padded = new Uint8Array(block);
  padded.set(shortKey);
  const inner = padded.map((byte) => byte ^ 0x36);
  const outer = padded.map((byte) => byte ^ 0x5c);
  return crypto.sha256(concat(outer, await crypto.sha256(concat(inner, message))));
}

const hashHex = async (crypto: SrpCrypto, hex: string): Promise<bigint> =>
  BigInt(`0x${bytesToHex(await crypto.sha256(hexToBytes(hex)))}`);

/** `k = H(N | g)`. */
export const multiplier = (crypto: SrpCrypto): Promise<bigint> => hashHex(crypto, padHex(N) + padHex(g));

export interface Ephemeral {
  /** Secret. Never leaves this module's caller. */
  a: bigint;
  /** Public, sent as `SRP_A`. */
  A: bigint;
}

export function ephemeral(crypto: SrpCrypto): Ephemeral {
  for (;;) {
    const a = BigInt(`0x${bytesToHex(crypto.randomBytes(128))}`);
    const A = modPow(g, a, N);
    // A ≡ 0 would let the server compute the key without the password. Unreachable with
    // a working RNG, and cheap to refuse.
    if (A % N !== 0n) return { a, A };
  }
}

/**
 * `EEE MMM d HH:mm:ss UTC yyyy`, the day **not** zero-padded — Cognito signs this string
 * and compares it byte for byte, so `Sep 07` fails on the seventh of every month.
 */
export function cognitoTimestamp(now: Date): string {
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const two = (value: number): string => String(value).padStart(2, '0');
  return (
    `${days[now.getUTCDay()]} ${months[now.getUTCMonth()]} ${now.getUTCDate()} ` +
    `${two(now.getUTCHours())}:${two(now.getUTCMinutes())}:${two(now.getUTCSeconds())} UTC ${now.getUTCFullYear()}`
  );
}

function base64ToBytes(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return bytes;
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

export interface VerifierInput {
  /** The part of the pool id after the underscore. */
  poolName: string;
  /** `USER_ID_FOR_SRP` from the challenge — the pool's internal username, not the email. */
  userIdForSrp: string;
  password: string;
  ephemeral: Ephemeral;
  /** From the challenge: `SRP_B`, `SALT` (hex) and `SECRET_BLOCK` (base64). */
  srpB: string;
  salt: string;
  secretBlock: string;
  now: Date;
}

/** The shared session key, 16 bytes — exported so a spec can check both halves agree. */
export async function sessionKey(
  crypto: SrpCrypto,
  input: Omit<VerifierInput, 'secretBlock' | 'now'>,
): Promise<Uint8Array> {
  const B = BigInt(`0x${input.srpB}`);
  // B ≡ 0 is a server (or an attacker) forcing the key; RFC 5054 says abort.
  if (B % N === 0n) throw new Error('SRP: the server sent B ≡ 0 mod N');
  const { a, A } = input.ephemeral;

  const u = await hashHex(crypto, padHex(A) + padHex(B));
  if (u === 0n) throw new Error('SRP: u is zero');

  const identity = bytesToHex(await crypto.sha256(utf8(`${input.poolName}${input.userIdForSrp}:${input.password}`)));
  const x = await hashHex(crypto, padHex(BigInt(`0x${input.salt}`)) + identity);
  const k = await multiplier(crypto);

  const S = modPow(B - k * modPow(g, x, N), a + u * x, N);

  // HKDF-SHA256, one block, info "Caldera Derived Key", truncated to 16 bytes.
  const prk = await hmacSha256(crypto, hexToBytes(padHex(u)), hexToBytes(padHex(S)));
  const okm = await hmacSha256(crypto, prk, concat(utf8('Caldera Derived Key'), Uint8Array.of(1)));
  return okm.slice(0, 16);
}

/** The `PASSWORD_VERIFIER` challenge responses. */
export async function passwordClaim(
  crypto: SrpCrypto,
  input: VerifierInput,
): Promise<{ PASSWORD_CLAIM_SIGNATURE: string; PASSWORD_CLAIM_SECRET_BLOCK: string; TIMESTAMP: string }> {
  const key = await sessionKey(crypto, input);
  const timestamp = cognitoTimestamp(input.now);
  const message = concat(
    utf8(input.poolName),
    utf8(input.userIdForSrp),
    base64ToBytes(input.secretBlock),
    utf8(timestamp),
  );
  return {
    PASSWORD_CLAIM_SIGNATURE: bytesToBase64(await hmacSha256(crypto, key, message)),
    PASSWORD_CLAIM_SECRET_BLOCK: input.secretBlock,
    TIMESTAMP: timestamp,
  };
}
